import React from "react";
import { useRequest } from "../contexts/request";
import { useMutation, useQuery } from "react-query";
import DOMPurify from "dompurify";

const OVERLAY_TYPE = {
  ALERT: "alert",
  TOPUP: "topup",
  SUBATHON: "subathon",
  MEDIASHARE: "mediashare",
  SOUNDBOARD: "soundboard",
  VOTE: "vote",
  QR: "qr",
  MILESTONE: "milestone",
  LEADERBOARD: "leaderboard",
  RECENT: "running_text",
  WHEEL: "wheel",
  WISHLIST: "wishlist",
  TOPUP: "topup",
  MABAR: "mabar",
  MABAR_PING: "mabar_ping",
  MABAR_POINT: "mabar_point",
  MABAR_DEDUCTIONS: "mabar_deductions",
  MABAR_SLOT: "mabar_slot",
  MABAR_REGULAR: "mabar_regular",
  MABAR_VIP: "mabar_vip",
  STORE_PURCHASE: "store_purchase",
  CHALLENGE: "challenge",
  MINI_GAME: "mini_game",
};

const COMMON_CONFIG = {
  noBorder: "no_border",
  noAmount: "no_amount",
  css: "css",
  html: "html",
  isSimple: "is_simple",
};

// Map old config key to new version
const FORWARD_MAPPER = {
  [OVERLAY_TYPE.SOUNDBOARD]: {
    ...COMMON_CONFIG,
    titleFont: "text2_font",
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "text2_color",
    notificationTimeout: "duration",
    template: "tts_template",
  },
  [OVERLAY_TYPE.ALERT]: {
    ...COMMON_CONFIG,
    titleFont: "text2_font",
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "text2_color",
    notificationTimeout: "duration",
    template: "tts_template",
  },
  [OVERLAY_TYPE.SUBATHON]: {
    ...COMMON_CONFIG,
    autoplay: "autoplay",
    backgroundColor: "background_color",
    color: "text1_color",
    bodyFont: "text1_font",
    fontWeight: "text1_weight",
    initHour: "init_h",
    initMinute: "init_m",
    initSecond: "init_s",
    rules: "rules",
  },
  [OVERLAY_TYPE.MEDIASHARE]: {
    ...COMMON_CONFIG,
    titleFont: "text2_font",
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "text2_color",
    notificationTimeout: "duration",
    template: "tts_template",
    showNSFW: "show_nsfw",
  },

  [OVERLAY_TYPE.TOPUP]: {
    ...COMMON_CONFIG,
    backgroundColor: "background_color",
    highlightColor: "text2_color",
    color: "text1_color",
    template: "tts_template",
    fontWeight: "text1_weight",
    duration: "duration",
    bodyFont: "text1_font",
  },

  [OVERLAY_TYPE.VOTE]: {
    ...COMMON_CONFIG,
    titleFont: "text2_font",
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "text2_color",
  },

  [OVERLAY_TYPE.QR]: {
    ...COMMON_CONFIG,
    topLabel: "text1",
    bodyFont: "text1_font",
    fontWeight: "text1_weight",
    color: "text1_color",
    bottomLabel: "text2",
    backgroundColor: "background_color",
    barcodeColor: "text2_color",
    username: "username",
  },

  [OVERLAY_TYPE.MILESTONE]: {
    ...COMMON_CONFIG,
    title: "text1",
    bodyFont: "text1_font",
    fontWeight: "text1_weight",
    color: "text1_color",
    titleFont: "text2_font",
    backgroundColor: "background_color",
    startDate: "start_datetime",
    target: "target",
  },

  [OVERLAY_TYPE.WISHLIST]: {
    ...COMMON_CONFIG,
    title: "text1",
    bodyFont: "text1_font",
    fontWeight: "text1_weight",
    color: "text1_color",
    titleFont: "text2_font",
    backgroundColor: "background_color",
    wishlistId: "wishlist_id",
    productName: "product_name",
    image: "image",
    target: "target",
  },

  [OVERLAY_TYPE.LEADERBOARD]: {
    ...COMMON_CONFIG,
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    titleFont: "text2_font",
    title: "text1",
    color: "text1_color",
    bodyFont: "text1_font",
    startDate: "start_datetime",
    mode: "mode",
  },

  [OVERLAY_TYPE.RECENT]: {
    ...COMMON_CONFIG,
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    text: "text1",
    color: "text1_color",
    bodyFont: "text1_font",
    speed: "speed",
  },

  [OVERLAY_TYPE.WHEEL]: {
    ...COMMON_CONFIG,
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    fontSize: "choices_fontsize",
    title: "text1",
    color: "text1_color",
    bodyFont: "choices_font",
    startAt: "init",
    spinTreshold: "target",
    choices: "choices",
    currency: "currency",
  },
  [OVERLAY_TYPE.TOPUP]: {
    ...COMMON_CONFIG,
    titleFont: "text2_font",
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "text2_color",
    notificationTimeout: "duration",
    template: "tts_template",
  },

  [OVERLAY_TYPE.MABAR]: {
    ...COMMON_CONFIG,
    titleFont: "text2_font",
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "text2_color",
    notificationTimeout: "duration",
    template: "tts_template",
  },
  [OVERLAY_TYPE.MABAR_PING]: {
    ...COMMON_CONFIG,
    titleFont: "text2_font",
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "text2_color",
    notificationTimeout: "duration",
    template: "tts_template",
  },

  [OVERLAY_TYPE.MABAR_POINT]: {
    ...COMMON_CONFIG,
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    titleFont: "text2_font",
    title: "text1",
    color: "text1_color",
    bodyFont: "text1_font",
  },
  [OVERLAY_TYPE.MABAR_DEDUCTIONS]: {
    ...COMMON_CONFIG,
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    titleFont: "text2_font",
    title: "text1",
    color: "text1_color",
    bodyFont: "text1_font",
  },
  [OVERLAY_TYPE.MABAR_SLOT]: {
    ...COMMON_CONFIG,
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    titleFont: "text2_font",
    title: "text1",
    color: "text1_color",
    bodyFont: "text1_font",
  },
   [OVERLAY_TYPE.MABAR_REGULAR]: {
     ...COMMON_CONFIG,
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    titleFont: "text2_font",
    title: "text1",
    color: "text1_color",
    bodyFont: "text1_font",
  },
   [OVERLAY_TYPE.MABAR_VIP]: {
    ...COMMON_CONFIG,
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    titleFont: "text2_font",
    title: "text1",
    color: "text1_color",
    bodyFont: "text1_font",
  },
  [OVERLAY_TYPE.STORE_PURCHASE]: {
    ...COMMON_CONFIG,
    titleFont: "text2_font",
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "text2_color",
    notificationTimeout: "duration",
    template: "tts_template",
  },
  [OVERLAY_TYPE.MINI_GAME]: {
    ...COMMON_CONFIG,
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "text2_color",
    notificationTimeout: "duration",
    template: "tts_template",
  },
  // No duration: the challenge timer is fixed and comes from challenge.duration in the event.
  [OVERLAY_TYPE.CHALLENGE]: {
    ...COMMON_CONFIG,
    bodyFont: "text1_font",
    backgroundColor: "background_color",
    fontWeight: "text1_weight",
    color: "text1_color",
    highlightColor: "highlight_color",
    template: "tts_template",
    anonymousLabel: "anonymous_label",
  },
};

const BACKWARD_MAPPER = {};

Object.entries(FORWARD_MAPPER).forEach(([type, config]) => {
  const backwardConfig = {};
  Object.entries(config).forEach(([oldField, newField]) => {
    backwardConfig[newField] = oldField;
  });
  BACKWARD_MAPPER[type] = backwardConfig;
});

const formatConfig = (direction, type, config) => {
  let mapper;
  if (direction === "forward") {
    mapper = FORWARD_MAPPER[type];
  } else {
    mapper = BACKWARD_MAPPER[type];
  }

  if (!mapper) {
    throw new Error(`${type}: OVERLAY NOT REGISTERED`);
  }

  const newConfig = {};

  Object.entries(config).forEach(([key, value]) => {
    const newKey = mapper[key];
    if (newKey) {
      newConfig[newKey] = value;
    }
  });
  return newConfig;
};

function formatBackward(type, config) {
  const conf = formatConfig("backward", type, config);

  return conf;
}

function formatForward(type, config) {
  return formatConfig("forward", type, config);
}

const useSaveOverlaySettings = (
  { overlayType, streamKey, wishlist, params },
  reactQueryProps = {}
) => {
  const { post } = useRequest();

  const saveOverlay = async (config) => {
    const filteredConfig = { ...config };
    if (config.isSimple) {
      delete filteredConfig["css"];
      delete filteredConfig["html"];
    }

    const formatted = formatForward(overlayType, filteredConfig);

    if (formatted["css"]) {
      formatted["css"] = DOMPurify.sanitize(formatted["css"]);
    }

    if (formatted["html"]) {
      formatted["html"] = DOMPurify.sanitize(formatted["html"]);
    }

    if (overlayType === "wishlist") {
      return await post(`template/wishlist/${wishlist.id}`, {
        json: formatted,
      });
    } else {
      return await post(`template/${overlayType}`, {
        json: formatted,
      });
    }

  };

  return useMutation(saveOverlay, { ...reactQueryProps });
};

const useOverlaySettings = (
  { overlayType, streamKey, wishlist, params },
  reactQueryProps = {}
) => {
  const { get } = useRequest();

  const key = `overlay-${overlayType}`;

  // useQuery with params and key
  
  return useQuery(
    [key, params, overlayType, streamKey],
    async (key) => {

      if (overlayType === "wishlist") {
        const wishlist_id = wishlist.id;
        const { json } = await get(
          `template/wishlist/${wishlist_id}`,
          { searchParams: { stream_key: streamKey } }
        );
        const { data } = json;
        const formatted = formatBackward(overlayType, data);

        return formatted;
      }

      const { json } = await get(
        `template/${overlayType}`,
        { searchParams: { stream_key: streamKey } }
      );
      const { data } = json;
      const formatted = formatBackward(overlayType, data);
      return formatted;
    },
    {
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      // 4xx (e.g. endpoint not ready) won't succeed on retry; react-query's default
      // 3 retries with backoff kept the page on "Loading" for ~7s.
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
      ...reactQueryProps,
      onError: (e) => {
        console.log(e);
        if (reactQueryProps.onError) {
          reactQueryProps.onError(e);
        }
      },
    }
  );
};

export { OVERLAY_TYPE, useOverlaySettings, useSaveOverlaySettings };
